import React, { useCallback, useContext, useMemo } from 'react';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from '../dataRoomApi';
import { DataRoomRFI, RFI_SEND_REMINDER_TRIGGER } from '../models/DataRoomRfi';
import { useTranslation } from 'react-i18next';
import { getArrayLength, getHasElementsInArray } from 'utils';
import {
  getIsRecipientRemindedRecently,
  getOutstandingReminderRecipients,
  getRecentlyRemindedRecipients,
} from '../subrouters/RFI/pages/RfiList/components/RfiRequestsTable/utils';
import RfiContext from '../providers/Rfi/Rfi.context';
import confirm from 'modules/confirm';

const useRfiSendAllReminders = (
  rfi: DataRoomRFI,
  onRfiUpdate: (rfi: DataRoomRFI) => void,
) => {
  const { _id, allRecipientsWithReminders } = rfi || {};

  const { t } = useTranslation();

  const { showActionUndo } = useContext(RfiContext);

  const outstandingRecipients = useMemo(
    () => getOutstandingReminderRecipients(rfi),
    [rfi],
  );

  const onUndoSendAllReminders = useCallback(async () => {
    await dataRoomApi.rfi.undoSendReminders(_id);

    onRfiUpdate({ ...rfi, allRecipientsWithReminders });
  }, [_id, allRecipientsWithReminders, onRfiUpdate, rfi]);

  const [sendAllReminders, loading] = useCallbackAsync(async () => {
    const recentlyRemindedRecipients = getRecentlyRemindedRecipients(rfi);

    const count = getArrayLength(outstandingRecipients);
    const skippedCount = getArrayLength(recentlyRemindedRecipients);

    const confirmed = await confirm({
      title: t('sendReminderConfirmTitle', {
        count,
        email: outstandingRecipients[0]?.email,
      }),
      content: (
        <>
          <p>{t('sendReminderConfirmDescription', { count })}</p>
          {skippedCount > 0 && (
            <p>
              {t('sendReminderConfirmSkippedDescription', {
                count: skippedCount,
              })}
            </p>
          )}
        </>
      ),
      submitBtnText: t('sendReminder'),
    });

    if (!confirmed) return;

    await dataRoomApi.rfi.sendReminders(
      _id,
      RFI_SEND_REMINDER_TRIGGER.REQUESTS_LIST,
    );

    onRfiUpdate({
      ...rfi,
      allRecipientsWithReminders: allRecipientsWithReminders.map((recipient) =>
        getIsRecipientRemindedRecently(recipient)
          ? recipient
          : { ...recipient, reminders: [...recipient.reminders, new Date()] },
      ),
    });

    showActionUndo(
      t('sendRemindersSuccess', { count }),
      onUndoSendAllReminders,
    );
  });

  return {
    sendAllReminders,
    loading,
    hasRecipients: getHasElementsInArray(outstandingRecipients),
  };
};

export default useRfiSendAllReminders;
